import { useCallback, useState } from "react";
import { ApiClientError, UNEXPECTED_ERROR_MESSAGE } from "../types/api";

export interface ServerAnswerState<T> {
  busy: boolean;
  /** The last refusal, carrying the server's code and sentence; null when none. */
  refusal: ApiClientError | null;
  /** Send `body`; a refusal is kept in `refusal`, never thrown. True when accepted. */
  run: (body: T) => Promise<boolean>;
  clear: () => void;
}

/**
 * Any rejection as an ApiClientError. A server refusal is kept as it came; any
 * other failure (a fault in the screen's own code, a shape that drifted)
 * carries the one generic sentence, never its own text (gate 2 Safety M1).
 */
export function asRefusal(err: unknown): ApiClientError {
  if (err instanceof ApiClientError) return err;
  return new ApiClientError(0, "UNKNOWN_ERROR", UNEXPECTED_ERROR_MESSAGE);
}

/** The sentence a screen shows for a rejection: the server's, else the generic one. */
export function refusalSentence(err: unknown): string {
  return asRefusal(err).message;
}

/**
 * One form's round trip to the server: busy while sending, the refusal kept.
 *
 * The form renders `refusal.message` (the server's sentence) and reads
 * `refusal.code` only to choose a control beside it, never to show it.
 */
export function useServerAnswer<T>(send: (body: T) => Promise<void>): ServerAnswerState<T> {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ApiClientError | null>(null);

  const run = useCallback(
    async (body: T) => {
      setBusy(true);
      setRefusal(null);
      try {
        await send(body);
        return true;
      } catch (err) {
        setRefusal(asRefusal(err));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [send],
  );

  const clear = useCallback(() => setRefusal(null), []);
  return { busy, refusal, run, clear };
}
